// Low-level markdown-directive (`:::name` … `:::`) scanning primitives plus
// the perspectiveBox / main-scenario-tab transforms that depend on them.

// Recognises a directive opener line and returns its fence + name.
export function directiveOpen(line: string): { fence: string; name: string } | undefined {
  const match = line.trim().match(/^(:{3,})([a-zA-Z][a-zA-Z0-9_-]*)$/)
  if (!match) return undefined
  return { fence: match[1], name: match[2] }
}

// Recognises a bare directive closer line and returns its fence string.
export function directiveClose(line: string): string | undefined {
  const match = line.trim().match(/^(:{3,})$/)
  return match?.[1]
}

// Index of the next line that isn't whitespace-only (or end-of-array).
export function nextNonBlankLine(lines: string[], startLine: number): number {
  let line = startLine
  while (line < lines.length && lines[line].trim() === '') line++
  return line
}

// A `:::perspectiveBox` that immediately closes then is followed by a
// `## Public…` heading lost its body to an over-eager close; drop that close.
function isStrayPerspectiveBoxClose(lines: string[], scanLine: number): number | undefined {
  const open = directiveOpen(lines[scanLine])
  if (open?.name !== 'perspectiveBox') return undefined
  const closeLine = nextNonBlankLine(lines, scanLine + 1)
  const closeFence = closeLine < lines.length ? directiveClose(lines[closeLine]) : undefined
  if (!closeFence || closeFence.length < open.fence.length) return undefined
  const contentLine = nextNonBlankLine(lines, closeLine + 1)
  if (contentLine >= lines.length || !/^##\s+Public/i.test(lines[contentLine].trim())) return undefined
  return closeLine
}

// Repairs every empty perspectiveBox in place so its `## Public…` body rejoins it.
export function repairEmptyPerspectiveBoxes(lines: string[]): void {
  let scanLine = 0
  while (scanLine < lines.length) {
    const strayClose = isStrayPerspectiveBoxClose(lines, scanLine)
    if (strayClose === undefined) { scanLine++; continue }
    lines.splice(strayClose, 1)
    scanLine = nextNonBlankLine(lines, strayClose)
  }
}

// Finds the closer matching the directive opened at `startLine`, tracking
// nested fences via a fence-length stack (a deeper/equal `:::` closes the top).
export function findDirectiveCloseLine(lines: string[], startLine: number): number | undefined {
  const open = directiveOpen(lines[startLine])
  if (!open) return undefined
  const fenceStack = [open.fence.length]
  let scanLine = startLine + 1
  while (scanLine < lines.length && fenceStack.length > 0) {
    updateFenceStack(fenceStack, lines[scanLine])
    if (fenceStack.length === 0) return scanLine
    scanLine++
  }
  return undefined
}

// One step of the fence-length stack: push nested opens, pop on a deep-enough close.
function updateFenceStack(fenceStack: number[], line: string): void {
  const nestedOpen = directiveOpen(line)
  const closeFence = directiveClose(line)
  if (nestedOpen) {
    fenceStack.push(nestedOpen.fence.length)
  } else if (closeFence && closeFence.length >= fenceStack[fenceStack.length - 1]) {
    fenceStack.pop()
  }
}

// Line index just past the directive block opened at `startLine`.
export function skipDirectiveBlock(lines: string[], startLine: number): number {
  const closeLine = findDirectiveCloseLine(lines, startLine)
  return closeLine === undefined ? startLine + 1 : closeLine + 1
}

// Longest `:::` fence appearing anywhere, so a wrapper can out-fence its nesting.
function maxDirectiveFenceLength(lines: string[]): number {
  let maxFenceLength = 0
  for (const line of lines) {
    const open = directiveOpen(line)
    const closeFence = directiveClose(line)
    const fenceLength = open?.fence.length || closeFence?.length || 0
    if (fenceLength > maxFenceLength) maxFenceLength = fenceLength
  }
  return maxFenceLength
}

type PBoxRange = { startLine: number; endLine: number; fenceLength: number }

// Locates every top-level `:::perspectiveBox` span (open line + close line).
function findPerspectiveBoxes(lines: string[]): PBoxRange[] {
  const perspectiveBoxes: PBoxRange[] = []
  let scanLine = 0
  while (scanLine < lines.length) {
    const open = directiveOpen(lines[scanLine])
    if (open?.name !== 'perspectiveBox') { scanLine++; continue }
    const endLine = findDirectiveCloseLine(lines, scanLine)
    if (endLine === undefined) { scanLine++; continue }
    perspectiveBoxes.push({ startLine: scanLine, endLine, fenceLength: open.fence.length })
    scanLine = endLine + 1
  }
  return perspectiveBoxes
}

// True when a line ends the markdown that should be pulled into a Scenario tab.
function isScenarioCaptureBoundary(line: string): boolean {
  const open = directiveOpen(line)
  return open?.name === 'perspectiveBox' || open?.name === 'branchpoint' || /^#{1,2} /.test(line.trim())
}

// Last line (exclusive) of the markdown that follows a perspectiveBox and
// belongs in its injected Scenario tab; skips over any nested directive blocks.
function findScenarioCaptureEnd(result: string[], captureStart: number): number {
  let captureEnd = captureStart
  while (captureEnd < result.length) {
    if (isScenarioCaptureBoundary(result[captureEnd])) break
    if (directiveOpen(result[captureEnd])) {
      captureEnd = skipDirectiveBlock(result, captureEnd)
      continue
    }
    captureEnd++
  }
  return captureEnd
}

// Drops leading/trailing blank lines from a captured Scenario-tab body.
function trimBlankEdges(captured: string[]): string[] {
  while (captured.length && captured[0].trim() === '') captured.shift()
  while (captured.length && captured[captured.length - 1].trim() === '') captured.pop()
  return captured
}

// Moves the markdown after one perspectiveBox into it as a `## Scenario` tab,
// re-fencing the box so it still out-fences whatever it now wraps.
function injectScenarioTabForBox(result: string[], pbox: PBoxRange): void {
  const captureStart = pbox.endLine + 1
  const captureEnd = findScenarioCaptureEnd(result, captureStart)
  if (captureEnd === captureStart) return
  const captured = trimBlankEdges(result.slice(captureStart, captureEnd))
  if (captured.length === 0) return

  result.splice(captureStart, captureEnd - captureStart)
  const insertion = ['', '## Scenario', '', ...captured, '']
  result.splice(pbox.endLine, 0, ...insertion)
  const newClosingLine = pbox.endLine + insertion.length
  const lineAfterClosing = newClosingLine + 1
  if (lineAfterClosing < result.length && result[lineAfterClosing].trim() !== '') {
    result.splice(lineAfterClosing, 0, '')
  }
  const pboxContent = result.slice(pbox.startLine + 1, newClosingLine)
  const pboxFenceLength = Math.max(pbox.fenceLength, maxDirectiveFenceLength(pboxContent) + 1)
  const pboxFence = ':'.repeat(pboxFenceLength)
  result[pbox.startLine] = `${pboxFence}perspectiveBox`
  result[newClosingLine] = pboxFence
}

// For each top-level :::perspectiveBox in `content`, capture the markdown
// that follows the box (until the next :::perspectiveBox / :::branchpoint /
// top-level h1 or h2 / end-of-content) and inject it back into that box as
// a final `## Scenario` tab.
export function injectMainScenarioTabs(content: string): string {
  const lines = content.split('\n')
  repairEmptyPerspectiveBoxes(lines)
  const perspectiveBoxes = findPerspectiveBoxes(lines)
  if (perspectiveBoxes.length === 0) return content

  const result = [...lines]
  // Iterate back-to-front so earlier boxes' line indices stay valid as we splice.
  for (let pboxIdx = perspectiveBoxes.length - 1; pboxIdx >= 0; pboxIdx--) {
    injectScenarioTabForBox(result, perspectiveBoxes[pboxIdx])
  }
  return result.join('\n')
}

// Scans from `startLine` (which sits at depth 1, just inside an already-opened
// directive) for its matching bare `:::` closer using a simple depth counter;
// `isOpener` decides which nested lines deepen the count. Returns the final
// scan index (the closer line when `closed`, else the array length) plus
// whether depth ever returned to zero — exactly the two facts the original
// inline loops branched on.
//
// This is intentionally a small state machine (the loop body is the machine).
// NOTE: this counts directive nesting depth and treats *any* bare `:::` as a
// close — it is deliberately distinct from findDirectiveCloseLine, which
// tracks fence *lengths*. Do not merge the two.
export function scanDirectiveDepthClose(
  lines: string[],
  startLine: number,
  isOpener: (trimmed: string) => boolean,
): { closeLine: number; closed: boolean } {
  let depth = 1
  let scanLine = startLine
  while (scanLine < lines.length && depth > 0) {
    const trimmed = lines[scanLine].trim()
    if (isOpener(trimmed)) depth++
    else if (trimmed === ':::') depth--
    if (depth > 0) scanLine++
  }
  return { closeLine: scanLine, closed: depth === 0 }
}

// Opener test shared by branchpoint / branchSummary scans: any `:::name`.
export const isAnyDirectiveOpener = (trimmed: string): boolean => /^:::[a-zA-Z]/.test(trimmed)
